<template>
  <div>
    <FlexCol gap-1 class="relative" ref="selectContainer">
      <slot name="container">
        <Label :label="label" :required="required" :error="error" v-if="label" />
        <InputArc :disabled="disabled" :error="error">
          <template #pre-icon>
          </template>
          <template #input>
            <Flex gap-1 class="w-full min-w-0 overflow-hidden items-center flex-nowrap">
              <slot :item="selectedItem" name="selected">
                <input
                  v-model="search"
                  :disabled="disabled"
                  :placeholder="!inputValue || inputValue.length == 0 ? placeholder ?? 'Selecione' : 'Digite para buscar'"
                  class="w-full min-w-0 flex-1 bg-transparent focus:outline-none focus:ring-0 placeholder:italic"
                  @focus="searchAutoComplete($event)"
                  @keypress.enter.prevent="searchValue()"
                  @input="searchAutoComplete($event)" />
                <Pill v-if="inputValue && inputValue.length > 0" class="bg-slate-200 whitespace-nowrap">
                  {{ inputValue.length }} selecionado<span v-if="inputValue.length > 1">s</span>
                </Pill>
              </slot>
            </Flex>
          </template>
          <template #pos-icon>
            <IconInput icon="fa-spinner fa-spin" class="border-l" v-if="loading" />
            <IconInput icon="fa-xmark" class="border-l" @click="clear()" v-if="inputValue != null && inputValue.length > 0 && !disabled && clearable" />
            <IconInput icon="fa-magnifying-glass" class="border-l" @click="toggleModal" :class="disabled ? 'cursor-not-allowed' : ''" />
          </template>
        </InputArc>
        <!--  Controle de Erros -->
        <Error :message="error" v-if="error" />
      </slot>

      <Teleport to="body">
        <TheTransition>
          <div v-if="autoCompleteBox" ref="popupRef" :style="popupStyle" class="fixed z-[2000] pt-2" data-floating-root="true">
            <Card rounded noSpace class="max-h-[240px] overflow-y-scroll text-sm border-slate-300">
              <div v-for="(item, i) in autoCompleteList" :key="i" @click="handleAutoCompleteSelect(item)" class="p-3 hover:bg-gray-100 cursor-pointer">
                <slot name="search-item" :item="item">
                  {{ resolveText(item) }}
                </slot>
              </div>
            </Card>
          </div>
        </TheTransition>
      </Teleport>
    </FlexCol>
    <!--  Modal de Zoom -->
    <Modal v-model="modalZoom" size="w-10/12 min-h-[90vh] h-full">
      <FlexCol gap-3 class="h-full min-h-0">
        <Title :title="`Busca de ${title}`">
          <div class="w-[420px]" v-if="simpleSearch">
            <InputArc>
              <template #pre-icon> </template>
              <template #input>
                <input v-model="search" :placeholder="placeholder" class="w-full bg-transparent focus:outline-none focus:ring-0 placeholder:italic" @keypress.enter.prevent="searchModal()" />
              </template>
              <template #pos-icon>
                <IconInput icon="fa-spinner fa-spin" class="border-l" v-if="loading" />
                <IconInput icon="fa-xmark" class="border-l" @click="clearAndSearch()" v-if="search != null" />
                <IconInput icon="fa-magnifying-glass" class="border-l" @click="searchModal()" />
              </template>
            </InputArc>
          </div>
          <div class="w-[180px]" v-if="!simpleSearch">
            <Filter v-model="filterObject" :filters="filters" />
          </div>
        </Title>
        <TableFull :headers="headers" :items="mergedItems" v-model="tableControl" dense class="!text-wrap" v-model:loading="loading" stickyActions>
          <template v-slot:['td-actions']="{ item }">
            {{ item.title }}
            <InputCheck dense :checked="allItems" @click="updateAll()"></InputCheck>
          </template>

          <template v-slot:actions="{ item }">
            <InputCheck @click="addItem(item)" dense disable-action :checked="checked(item)" ></InputCheck>
          </template>
          
          <!-- Passa todos os slots recebidos para o TableFull -->
          <template v-for="(_, name) in $slots" :key="name" v-slot:[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
          
        </TableFull>
        <slot name="action">
          <Flex justify-end class="py-3">
            <Button label="Selecionar" icon="fa-solid fa-circle-plus" primary @click="modalZoom = false" />
          </Flex>
        </slot>
      </FlexCol>
    </Modal>
  </div>
</template>

<script setup>
import base from '@/plugins/zoom'
import { get } from '@/crud.js'
import { computed, defineEmits, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'

// Definição de Props do Componente
const props = defineProps({
  clearable: Boolean,
  database: String,
  disabled: Boolean,
  error: [Array, String],
  filter: Array,
  itemText: String,
  itemValue: String,
  itemAutocomplete: [String, Array],
  tableFilter: String,
  label: String,
  modelValue: { type: Array, default: [] },
  placeholder: String,
  required: Boolean,
  ignoreInitialFilters: Boolean,
  show: Boolean,
  selectAll: Boolean,
  zoomTitle: String,
  zoomHeaders: Array,
})
// Data
const actualPage = ref(1)
const emits = defineEmits(['update:modelValue', 'update:show'])
const filters = ref([])
const filterObject = ref([])
const inputText = ref('')
const limit = ref(14)
const selectContainer = ref(null)
const popupRef = ref(null)
const popupStyle = ref({})
const autoCompleteAnchor = ref(null)

const loading = ref(false)
const items = ref([])
const headers = ref([])
const modalZoom = ref(false)
const order = ref({ direction: 'ASC', field: 'id' })
const pages = ref(null)
const route = ref(null)

const autoCompleteBox = ref(false)
const autoCompleteList = ref([])
const inputValue = ref()
const selectedItem = ref()

const title = ref('')
const totalItems = ref(null)

const search = ref(null)
const database = props.database
const simpleSearch = ref(!!base?.[database]?.simpleSearch)

const allItems = ref(false)
const toSelect = ref([])

const getInitialFilters = () => {
  if (props.ignoreInitialFilters || !base?.[database]?.initialFilters) return []
  return Array.isArray(base[database].initialFilters) ? [...base[database].initialFilters] : []
}

// O valor recebido pode ser recriado pelo componente pai a cada render.
// Comparar por identidade nesse caso faz o v-model emitir o mesmo conjunto
// repetidamente e cria um ciclo de atualizações entre pai e filho.
const sameSelection = (left, right) => {
  const leftItems = Array.isArray(left) ? left : []
  const rightItems = Array.isArray(right) ? right : []

  if (leftItems.length !== rightItems.length) return false

  return leftItems.every((item, index) => item?.[props.itemValue] === rightItems[index]?.[props.itemValue])
}

//  Controle de Tabela
const tableControl = ref({
  page: 1,
  total: 0,
  limit: 50,
  pages: 0,
  order: [],
})

const checkItems = computed(() => {
  const hasItems = selectedItem.value && totalItems.value
  if (toSelect.value && toSelect.value.length > 0) {
    if (selectedItem.value == toSelect.value) {
      return true
    }
  }
  if (hasItems && totalItems.value <= selectedItem.value.length) {
    return true
  }

  return false
})

// Mescla os itens da página atual com os valores modificados do inputValue
const mergedItems = computed(() => {
  if (!items.value || !Array.isArray(items.value)) {
    return []
  }

  return items.value.map(item => {
    // Procura se este item está no inputValue (itens selecionados/modificados)
    const selectedItem = inputValue.value?.find(
      selected => selected[props.itemValue] === item[props.itemValue]
    )
    
    // Se encontrou, usa o item modificado; senão usa o item original
    return selectedItem || item
  })
})

// Métodos

const clear = () => {
  autoCompleteBox.value = false
  autoCompleteAnchor.value = null
  filterObject.value = getInitialFilters()
  inputText.value = null
  inputValue.value = []
  selectedItem.value = []
  search.value = null
  emits('update:modelValue', inputValue.value)
}

const updatePopupPosition = () => {
  const containerElement = selectContainer.value?.$el ?? selectContainer.value
  const anchorElement = autoCompleteAnchor.value ?? containerElement
  if (!autoCompleteBox.value || !anchorElement || typeof anchorElement.getBoundingClientRect !== 'function') return

  const anchorRect = anchorElement.getBoundingClientRect()
  const containerRect = containerElement?.getBoundingClientRect?.() ?? anchorRect

  popupStyle.value = {
    left: `${containerRect.left}px`,
    width: `${containerRect.width}px`,
    top: `${anchorRect.bottom + 8}px`,
  }
}

const setAutoCompleteAnchor = (event) => {
  if (event?.target && typeof event.target.getBoundingClientRect === 'function') {
    autoCompleteAnchor.value = event.target
  }
}

const handleClickOutside = (event) => {
  const containerElement = selectContainer.value?.$el ?? selectContainer.value
  const popupElement = popupRef.value?.$el ?? popupRef.value
  const anchorElement = autoCompleteAnchor.value

  if (containerElement?.contains(event.target)) return
  if (anchorElement?.contains?.(event.target)) return
  if (popupElement?.contains(event.target)) return

  autoCompleteBox.value = false
}

const deployZoom = async () => {
  await startZoom()
  await startValue()
}

const loadItems = async (page = 1) => {
  loading.value = true

  const query = {
    params: {
      page: tableControl.value.page,
      limit: tableControl.value.limit,
      order: tableControl.value.order,
      filters: [],
    },
  }

  let raw = []

  const isEmptyValue = (value) => {
    if (value === null || value === undefined || value === '') return true
    if (Array.isArray(value) && value.length === 0) return true
    return false
  }

  const staticFilters = []
  if (base?.[database]?.tableFilter && Array.isArray(base[database].tableFilter)) {
    staticFilters.push(...base[database].tableFilter)
  }
  if (props.tableFilter && Array.isArray(props.tableFilter)) {
    staticFilters.push(...props.tableFilter)
  }

  const allFilters = [...(props.filter || []), ...staticFilters, ...(Array.isArray(filterObject.value) ? filterObject.value : [])]
  query.params.filters = allFilters.filter(f => !isEmptyValue(f.value))

  if (query.params.filters.length == 0) {
    delete query.params.filters
  }

  raw = await get(`${route.value}`, query)

  loading.value = false
  items.value = raw.data

  tableControl.value.pages = Math.ceil(raw.total / tableControl.value.limit)
  tableControl.value.total = raw.total
}

const loadAll = async () => {
  const query = {
    params: {
      filters: [],
    },
  }

  let raw = []

  const staticFilters = []
  if (base?.[database]?.tableFilter && Array.isArray(base[database].tableFilter)) {
    staticFilters.push(...base[database].tableFilter)
  }
  if (props.tableFilter && Array.isArray(props.tableFilter)) {
    staticFilters.push(...props.tableFilter)
  }

  query.params.filters = [...(props.filter || []), ...staticFilters, ...(Array.isArray(filterObject.value) ? filterObject.value : [])]

  if (query.params.filters.length == 0) {
    delete query.params.filters
  }

  raw = await get(route.value, query)
  totalItems.value = raw.total
  toSelect.value = new Set(raw.data)
}

const startValue = async () => {
  const field = props.itemValue
  const value = props.modelValue

  const selectedValues = (Array.isArray(value) ? value : [value])
    .map((item) => (item && typeof item === 'object' ? item[field] : item))
    .filter((item) => item !== null && item !== undefined && item !== '')

  if (!field || !selectedValues.length) return

  const filterObject = {
    column: props.itemValue,
    operator: 'in',
    value: selectedValues,
  }

  const query = {
    params: {
      filters: [filterObject],
    },
  }

  try {
    const selectedItem = await get(route.value || props.database, query)
    const selectedItemData = Array.isArray(selectedItem?.data) ? selectedItem.data : []

    if (selectedItemData) {
      // inputText.value = resolveText(selectedItemData)
      inputValue.value = selectedItemData
    }
  } catch (error) {
    console.error('Erro ao buscar valor inicial:', error)
  }
}

const startZoom = async () => {
  const database = props.database

  if (database && base && base[database]) {
    route.value = base[database].route
    const actionHeader = { title: 'Ações', field: 'actions', itemClass: 'w-[280px]', headerClass: 'text-center justify-center', disableOrder: true }
    const zoomHeaders = props.zoomHeaders || base[database].headers
    const visibleHeaders = database === 'employee'
      ? zoomHeaders.filter((header) => header.field !== 'status')
      : zoomHeaders
    headers.value = [...visibleHeaders, actionHeader]
    filters.value = base[database].filters
    title.value = props.zoomTitle || base[database].title || ''
    filterObject.value = getInitialFilters()
    await loadItems()
  }
}

const toggleModal = () => {
  if (loading.value) {
    return
  }
  if (!props.disabled) {
    autoCompleteBox.value = false
    modalZoom.value = !modalZoom.value
    emits('update:show', modalZoom.value)
  }
}

const addItem = (item) => {
  const itemVal = item[props.itemValue]

  if (!Array.isArray(inputValue.value)) {
    inputValue.value = []
    selectedItem.value = []
  }

  if (Array.isArray(inputValue.value)) {
    const selectedIndex = inputValue.value.findIndex((i) => i[props.itemValue] === itemVal)
    if (selectedIndex === -1) {
      // Adiciona o item (pode já ter propriedades modificadas se vier do mergedItems)
      inputValue.value.push(item)
    } else {
      // Remove o item mantendo as modificações para outros itens
      inputValue.value.splice(selectedIndex, 1)
    }
  } else {
    inputValue.value = [item]
  }

  if (Array.isArray(selectedItem.value)) {
    const selectedIndex = selectedItem.value.findIndex((i) => i[props.itemValue] === itemVal)
    if (selectedIndex === -1) {
      selectedItem.value.push(item)
    } else {
      selectedItem.value.splice(selectedIndex, 1)
    }
  } else {
    selectedItem.value = [item]
  }
}

const checked = (item) => {
  const itemVal = item[props.itemValue]
  if (Array.isArray(inputValue.value)) {
    const index = inputValue.value.findIndex((i) => i[props.itemValue] === itemVal)
    return index >= 0 ? true : false
  }
  return false
}

const updateAll = async () => {
  allItems.value = !allItems.value
  loading.value = true
  try {
    if (allItems.value && toSelect.value) {
      await loadAll()

      if (!Array.isArray(inputValue.value)) {
        inputValue.value = []
        selectedItem.value = []
      }
      let selectedValueSet = new Set(inputValue.value.map((item) => item[props.itemValue])) || new Set()
      let itemsToAdd = []
      for (const item of toSelect.value) {
        if (!selectedValueSet.has(item)) {
          itemsToAdd.push(item)
        }
      }
      if (itemsToAdd.length > 0) {
        inputValue.value = [...itemsToAdd]
        selectedItem.value = [...itemsToAdd]
      }
    } else {
      inputValue.value = []
      selectedItem.value = []
    }
  } finally {
    loading.value = false
  }
}

const clearAndSearch = async()=>{
  search.value = null
  await searchModal()
}

const getSearchFilters = () => {
  if (!search.value || search.value.length < 2) return getInitialFilters()

  if (Array.isArray(props.itemAutocomplete)) {
    return [
      ...getInitialFilters(),
      {
        column: props.itemAutocomplete,
        value: search.value,
        operator: 'or like',
      },
    ]
  }

  return [
    ...getInitialFilters(),
    {
      column: props.itemAutocomplete ?? props.itemText ?? props.itemValue,
      value: search.value,
      operator: 'like',
    },
  ]
}

const searchModal = async () => {
  autoCompleteBox.value = false
  autoCompleteAnchor.value = null
  filterObject.value = getSearchFilters()
  await loadItems()
}

const searchValue = async () => {
  if (!search.value || search.value.length < 2) {
    autoCompleteBox.value = false
    return
  }

  await searchAutoComplete()

  if (items.value?.length === 1) {
    handleAutoCompleteSelect(items.value[0])
  } else if (items.value?.length > 1 && !modalZoom.value) {
    modalZoom.value = true
    emits('update:show', modalZoom.value)
  }
}

const resolveText = (item) => {
  if (!item) return ''

  if (Array.isArray(props.itemAutocomplete) && props.itemAutocomplete.length > 0) {
    const primaryField = props.itemAutocomplete[0]
    const primaryValue = item?.[primaryField]
    const textValue = props.itemText ? item?.[props.itemText] : null
    return textValue ? `${primaryValue} - ${textValue}` : `${primaryValue ?? ''}`
  }

  const valueField = props.itemValue ? item?.[props.itemValue] : null
  const textField = props.itemText ? item?.[props.itemText] : null

  if (valueField != null && textField) return `${valueField} - ${textField}`
  if (textField) return `${textField}`
  if (valueField != null) return `${valueField}`
  return ''
}

const handleAutoCompleteSelect = (item) => {
  addItem(item)
  autoCompleteBox.value = false
  autoCompleteAnchor.value = null
  search.value = null
  filterObject.value = getInitialFilters()
}

const searchAutoComplete = async (event) => {
  setAutoCompleteAnchor(event)

  if (!search.value || search.value.length < 2) {
    autoCompleteBox.value = false
    filterObject.value = getInitialFilters()
    return
  }
  filterObject.value = getSearchFilters()

  await loadItems()

  autoCompleteList.value = items.value
  if (!items.value || items.value.length === 0) {
    autoCompleteBox.value = false
    filterObject.value = getInitialFilters()
  } else {
    autoCompleteBox.value = true
    nextTick(updatePopupPosition)
  }

  // autoCompleteBox.value = false
}
// Watchers
// Watchers
watch(autoCompleteBox, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition)
    setTimeout(() => document.addEventListener('click', handleClickOutside), 0)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

watch(
  () => tableControl.value,
  () => {
    loadItems()
  },
  { deep: true }
)
watch(
  filterObject,
  (val, oldVal) => {
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      const isEmptyValue = (value) => {
        if (value === null || value === undefined || value === '') return true
        if (Array.isArray(value) && value.length === 0) return true
        return false
      }
      if (Array.isArray(filterObject.value)) {
        const cleanedFilters = filterObject.value.filter(f => !isEmptyValue(f.value))
        if (cleanedFilters.length !== filterObject.value.length) {
          filterObject.value = cleanedFilters
          return
        }
      }

      tableControl.value.page = 1
      loadItems()
    }
  },
  { deep: true }
)

watch(
  () => inputValue.value,
  (value) => {
    if (Array.isArray(value) && value.length > 0) {
      if (!sameSelection(value, props.modelValue)) {
        emits('update:modelValue', value)
      }
    } else if (Array.isArray(value) && value.length == 0) {
      if (Array.isArray(props.modelValue) && props.modelValue.length > 0) {
        emits('update:modelValue', null)
      }
    }
  },
  { deep: true }
)

watch(modalZoom, (value) => {
  emits('update:show', value)
})

watch(
  () => props.modelValue,
  (value) => {
    if (value !== inputValue.value) {
      // A seleção interna é mutada ao marcar/desmarcar itens. Nunca reutilize
      // a referência recebida do v-model, ou a alteração passa a modificar a
      // prop antes do emit e a comparação entende que nada mudou.
      inputValue.value = Array.isArray(value) ? [...value] : []
      selectedItem.value = Array.isArray(value) ? [...value] : []
    }
  },
  { deep: true }
)

watch(
  () => props.show,
  (value) => {
    if (value != modalZoom.value) {
      modalZoom.value = value
      emits('update:show', modalZoom.value)
      deployZoom()
    }
  },
  { immediate: true }
)

watch(
  () => props.filter,
  (newVal, oldVal) => {
    const changed = JSON.stringify(newVal) !== JSON.stringify(oldVal)
    if (changed) {
      tableControl.value.page = 1
      loadItems()
    }
  },
  { deep: true }
)

//facilitar sincronização de checkItems com allItems usando so 1 watch
watchEffect(() => {
  if (checkItems.value !== allItems.value) {
    allItems.value = checkItems.value
  }
})

// Hooks
onMounted(async () => {
  inputValue.value = Array.isArray(props.modelValue) ? [...props.modelValue] : []
  selectedItem.value = Array.isArray(props.modelValue) ? [...props.modelValue] : []
  await deployZoom()
  window.addEventListener('resize', updatePopupPosition)
  window.addEventListener('scroll', updatePopupPosition, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePopupPosition)
  window.removeEventListener('scroll', updatePopupPosition, true)
})
</script>
